import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import React, { FC } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageBoxFuture from '../shared/image-box-future';
import { Pagination } from 'swiper';
import { Specialty } from '@/logic/services/experiences';
import { pxToRem } from '@/theme/typography';

interface experiencesProps {
  Specialty: Specialty[];
}

export const ExperienceSpecialitiesSlider: FC<experiencesProps> = experiencesProps => {
  const theme = useTheme();

  return (
    <Box
      component='div'
      sx={{
        width: '100%',
        '& .swiper': {
          px: { laptop: pxToRem(15), tablet: pxToRem(0) },
          pb: { tablet: pxToRem(86), mobile: '50px' },
          position: 'relative',
          direction: 'ltr',
        },
        '& .swiper-wrapper': {
          alignItems: ' center',
        },
        '& .swiper-slide.swiper-slide-next div.MuiBox-root': {
          height: '100%',
        },
        '& .swiper-slide.swiper-slide-next  div.MuiBox-root::after, .swiper-slide div.MuiBox-root div.MuiBox-root::after':
          {
            width: '0%',
          },
        '& .swiper-horizontal>.swiper-pagination-bullets, .swiper-pagination-bullets.swiper-pagination-horizontal, .swiper-pagination-custom, .swiper-pagination-fraction':
          {
            bottom: { tablet: pxToRem(48), mobile: '24px' },
          },
        '& .swiper-slide': {
          display: 'flex',
          [theme.breakpoints.down(768)]: {
            display: 'flex',
            justifyContent: 'center',
          },
        },
        '& .swiper-slide a': { display: 'none' },
        '& .swiper-slide.swiper-slide-next a': { display: 'flex' },
        '& .swiper-pagination-bullet': {
          width: { tablet: pxToRem(12), mobile: '6px' },
          height: { tablet: pxToRem(12), mobile: '6px' },
          background: theme.palette.grey[30],
          backdropFilter: ' blur(5px)',
          mx: pxToRem(6),
          borderRadius: '100%',
          cursor: 'pointer',
        },
        '& .swiper-pagination-bullet-active': {
          background: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          width: { tablet: pxToRem(14), mobile: '8px' },
          height: { tablet: pxToRem(14), mobile: '8px' },
        },
        '& .swiper-pagination': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
    >
      <Swiper
        spaceBetween={46}
        slidesPerView={3}
        modules={[Pagination]}
        pagination={{ clickable: true }}
        scrollbar={{ draggable: true }}
        breakpoints={{
          0: {
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 2,
          },
          992: {
            slidesPerView: 3,
          },
        }}
      >
        {experiencesProps?.Specialty?.map((item: Specialty) => (
          <Box key={item.id} component='section'>
            <SwiperSlide key={item.id}>
              <Box
                key={item.id}
                component='div'
                sx={{
                  background: theme.palette.layout.bgSecondary,
                  boxShadow: theme.customShadows.cardShadow,
                  borderRadius: '25px',
                  position: 'relative',
                  cursor: 'grab',
                  marginBottom: pxToRem(24),
                }}
              >
                <ImageBoxFuture
                  img={item.cover}
                  title={item?.name}
                  layout='intrinsic'
                  objectFit='cover'
                  width='1920px'
                  height='1080px'
                  sx={{
                    borderRadius: '20px',
                    overflow: 'hidden ',
                  }}
                />

                <Box
                  component='div'
                  sx={{
                    p: pxToRem(24),
                    pt: pxToRem(22),
                  }}
                >
                  <Tooltip title={item.name}>
                    <Typography
                      component='h3'
                      sx={{
                        fontSize: { tablet: pxToRem(28), mobile: '18px' },
                        color: theme.palette.grey[90],
                        mb: pxToRem(24),
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Tooltip>
                  <Typography
                    component='p'
                    sx={{
                      fontSize: { tablet: pxToRem(24), mobile: '16px' },
                      color: theme.palette.grey[50],
                      height: { tablet: pxToRem(144), mobile: '96px' },
                      mb: pxToRem(32),
                      display: '-webkit-box',
                      WebkitLineClamp: '4',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </SwiperSlide>
          </Box>
        ))}
      </Swiper>
    </Box>
  );
};

export default ExperienceSpecialitiesSlider;
